import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { UwIcon } from '../../shared/ui/icon/uw-icon';
import { UwBadge } from '../../shared/ui/badge/uw-badge';
import { UwButton } from '../../shared/ui/button/uw-button';
import { UwProgress } from '../../shared/ui/progress/uw-progress';
import { UwAvatar } from '../../shared/ui/avatar/uw-avatar';
import { EXTRACTION_PREVIEW, INGESTION_ROWS, SUBMISSIONS } from '../../core/services/mock-data';
import { AccountStore } from '../../core/stores/account.store';
import { ToastService } from '../../core/services/toast.service';
import type { Submission } from '../../core/models/submission.model';
import type { RiskTone } from '../../core/models/types';
import { SubmissionTableLabComponent } from './submission-table-lab/submission-table-lab.component';

interface IntakeStat {
  label: string;
  value: string;
  delta?: string;
  dir?: 'up' | 'down';
}

interface IntakeChip {
  key: string;
  label: string;
  count: number;
  icon?: string;
  danger?: boolean;
  pullRight?: boolean;
}

const INGESTION_STATS: IntakeStat[] = [
  { label: 'Received today', value: '38', delta: '+12% vs avg', dir: 'up' },
  { label: 'Auto-routed', value: '29', delta: '76% straight-through' },
  { label: 'Needs review', value: '6', delta: 'Low OCR confidence' },
  { label: 'Duplicate flagged', value: '3', delta: 'Matched existing acct' },
];

const INGESTION_CHIPS: IntakeChip[] = [
  { key: 'all', label: 'All sources', count: 38 },
  { key: 'email', label: 'Email', count: 24, icon: 'inbox' },
  { key: 'portal', label: 'Broker portal', count: 9, icon: 'globe' },
  { key: 'api', label: 'API / EDI', count: 5, icon: 'layers' },
  { key: 'review', label: 'Needs review', count: 6, danger: true, pullRight: true },
];

const SOURCE_ICONS: Record<string, string> = { email: 'inbox', portal: 'globe', api: 'layers' };

const SUBMISSION_STATS: IntakeStat[] = [
  { label: 'Open submissions', value: '47' },
  { label: 'Due today', value: '8', delta: '2 at risk', dir: 'down' },
  { label: 'Overdue', value: '2', delta: 'Escalated to manager', dir: 'down' },
  { label: 'Avg. cycle time', value: '5.4 days', delta: '−0.6 days vs Q2', dir: 'up' },
];

const SUBMISSION_CHIPS: IntakeChip[] = [
  { key: 'mine', label: 'My queue', count: 14 },
  { key: 'team', label: 'Team queue', count: 47 },
  { key: 'new', label: 'New business', count: 31 },
  { key: 'renewal', label: 'Renewal', count: 16 },
];

@Component({
  selector: 'uw-classic-ingestion-page',
  standalone: true,
  imports: [UwIcon, UwBadge, UwButton, UwProgress],
  template: `
    <div class="mx-auto max-w-6xl space-y-4 p-4 sm:p-6">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 class="typo-heading text-primary">Ingestion</h1>
          <p class="text-[11.5px] text-text-muted">Incoming submissions, broker sources and extraction quality.</p>
        </div>
        <div class="flex gap-1.5">
          <uw-button variant="secondary" size="sm" icon="file" (click)="manualUpload()">Manual upload</uw-button>
          <uw-button variant="primary" size="sm" icon="plus" (click)="connectSource()">Connect new source</uw-button>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        @for (s of stats; track s.label) {
          <div class="rounded-xl border border-line bg-surface p-4 shadow-sm">
            <p class="typo-label">{{ s.label }}</p>
            <p class="mt-1 typo-mono text-[22px] font-bold text-primary">{{ s.value }}</p>
            @if (s.delta) {
              <p
                class="mt-0.5 flex items-center gap-1 text-[11px]"
                [class]="s.dir === 'up' ? 'text-success' : s.dir === 'down' ? 'text-warning' : 'text-text-muted'"
              >
                @if (s.dir === 'up') {
                  <uw-icon name="trending-up" [size]="11" />
                } @else if (s.dir === 'down') {
                  <uw-icon name="trending-down" [size]="11" />
                }
                {{ s.delta }}
              </p>
            }
          </div>
        }
      </div>

      <div class="flex flex-wrap items-center gap-1.5">
        @for (chip of chips; track chip.key) {
          <button
            class="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors"
            [class]="chipClass(chip)"
            [class.ml-auto]="chip.pullRight"
            (click)="setChip(chip.key)"
          >
            @if (chip.icon) {
              <uw-icon [name]="chip.icon" [size]="12" />
            }
            {{ chip.label }}
            <span class="typo-mono">· {{ chip.count }}</span>
          </button>
        }
      </div>

      <div class="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full border-collapse text-[12.5px]">
            <thead>
              <tr class="bg-surface-sunken/60">
                <th class="typo-label px-3 py-2.5 text-left">Source</th>
                <th class="typo-label px-3 py-2.5 text-left">Sender / Broker</th>
                <th class="typo-label px-3 py-2.5 text-left">Subject / Reference</th>
                <th class="typo-label px-3 py-2.5 text-left">Received</th>
                <th class="typo-label px-3 py-2.5 text-left">Detected forms</th>
                <th class="typo-label px-3 py-2.5 text-left">Extraction confidence</th>
                <th class="typo-label px-3 py-2.5 text-left">Status</th>
                <th class="w-16 px-3 py-2.5 text-left"></th>
              </tr>
            </thead>
            <tbody>
              @for (row of filteredRows(); track row.id) {
                <tr class="border-t border-line transition-colors hover:bg-surface-sunken/50">
                  <td class="px-3 py-2.5 align-middle">
                    <uw-icon [name]="sourceIcon(row.source)" [size]="15" class="text-text-muted" />
                  </td>
                  <td class="px-3 py-2.5 align-middle font-semibold text-primary">{{ row.sender }}</td>
                  <td class="px-3 py-2.5 align-middle text-secondary">{{ row.subject }}</td>
                  <td class="px-3 py-2.5 align-middle text-text-muted">{{ row.received }}</td>
                  <td class="px-3 py-2.5 align-middle text-text-muted">{{ row.forms }}</td>
                  <td class="px-3 py-2.5 align-middle">
                    @if (row.confidence > 0) {
                      <div class="flex w-32 items-center gap-2">
                        <div class="w-16">
                          <uw-progress [value]="row.confidence" [tone]="confTone(row.confidence)" label="Extraction confidence" />
                        </div>
                        <span class="typo-mono text-[11px] text-secondary">{{ row.confidence }}%</span>
                      </div>
                    } @else {
                      <span class="text-text-muted">—</span>
                    }
                  </td>
                  <td class="px-3 py-2.5 align-middle">
                    <uw-badge [tone]="toneOf(row.tone)">{{ row.status }}</uw-badge>
                  </td>
                  <td class="px-3 py-2.5 align-middle">
                    <button class="text-[11px] font-semibold text-ai transition-colors hover:underline" (click)="review(row)">
                      Review
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>

      <div class="rounded-xl border border-line bg-surface shadow-sm">
        <div class="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
          <h3 class="typo-title flex items-center gap-1.5 text-primary">
            <uw-icon name="sparkles" [size]="14" class="text-ai" />
            {{ extraction.title }}
          </h3>
          <div class="flex gap-1.5">
            <uw-button variant="secondary" size="sm" (click)="openEmail()">Open original email</uw-button>
            <uw-button variant="primary" size="sm" icon="check" (click)="acceptExtraction()">Accept & route to queue</uw-button>
          </div>
        </div>
        <div class="grid gap-4 p-4 lg:grid-cols-2">
          <div>
            <p class="typo-label mb-1">Fields extracted (ACORD 140 + SOV)</p>
            @for (f of extraction.fields; track f.label) {
              <div class="flex items-center justify-between border-b border-line py-2 last:border-b-0">
                <div>
                  <p class="text-[10.5px] font-semibold uppercase tracking-wide text-text-muted">{{ f.label }}</p>
                  <p class="text-[12.5px] font-semibold text-primary">{{ f.value }}</p>
                </div>
                <span class="rounded-md px-1.5 py-0.5 text-[10px] font-bold" [class]="confClass(f.confidence)">{{ f.confidence }}%</span>
              </div>
            }
          </div>
          <div class="flex items-start gap-2 rounded-lg border border-warning/25 bg-warning-soft/40 p-3 lg:self-start">
            <uw-icon name="sparkles" [size]="13" class="mt-0.5 shrink-0 text-warning" />
            <div>
              <p class="text-[12px] font-semibold text-warning">Low-confidence field flagged.</p>
              <p class="mt-0.5 text-[11.5px] leading-snug text-secondary">{{ extraction.note }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class ClassicIngestionPage {
  private readonly toast = inject(ToastService);

  protected readonly stats = INGESTION_STATS;
  protected readonly chips = INGESTION_CHIPS;
  protected readonly rows = INGESTION_ROWS;
  protected readonly extraction = EXTRACTION_PREVIEW;

  protected readonly activeChip = signal('all');

  protected readonly filteredRows = computed(() => {
    const key = this.activeChip();
    if (key === 'review') return this.rows.filter((r) => r.tone === 'warning' || r.tone === 'danger');
    if (key === 'all') return this.rows;
    return this.rows.filter((r) => r.source === key);
  });

  protected sourceIcon(source: string): string {
    return SOURCE_ICONS[source] ?? 'inbox';
  }

  protected toneOf(tone: string): RiskTone {
    const t = tone as RiskTone;
    return ['success', 'warning', 'danger', 'info', 'neutral'].includes(t) ? t : 'neutral';
  }

  protected confTone(confidence: number): RiskTone {
    return confidence > 85 ? 'success' : confidence > 50 ? 'warning' : 'danger';
  }

  protected confClass(confidence: number): string {
    const tones: Record<string, string> = {
      success: 'bg-success-soft text-success',
      warning: 'bg-warning-soft text-warning',
      danger: 'bg-danger-soft text-danger',
    };
    return tones[this.confTone(confidence)];
  }

  protected chipClass(chip: IntakeChip): string {
    if (this.activeChip() === chip.key) return 'border-primary/40 bg-primary-soft text-primary';
    if (chip.danger) return 'border-danger/40 text-danger hover:bg-danger-soft';
    return 'border-line-strong text-secondary hover:bg-surface-sunken hover:text-primary';
  }

  protected setChip(key: string): void {
    this.activeChip.set(key);
  }

  protected review(row: (typeof INGESTION_ROWS)[number]): void {
    this.toast.info('Extraction review', `Reviewing extraction from ${row.sender}.`);
  }

  protected manualUpload(): void {
    this.toast.info('Manual upload', 'Drag & drop ACORD forms to begin ingestion.');
  }

  protected connectSource(): void {
    this.toast.info('Connect new source', 'Setup guide for a new broker portal or API feed.');
  }

  protected openEmail(): void {
    this.toast.info('Open original email', 'Opening the source email in the document viewer.');
  }

  protected acceptExtraction(): void {
    this.toast.success('Extraction accepted', 'Nolan Logistics routed to the submission queue.');
  }
}

@Component({
  selector: 'uw-classic-submissions-page',
  standalone: true,
  imports: [UwIcon, UwBadge, UwButton, UwAvatar, UwProgress, SubmissionTableLabComponent],
  template: `
    <div class="mx-auto max-w-8xl space-y-4 p-4 sm:p-6">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 class="typo-heading text-primary">Submission Management</h1>
          <p class="text-[11.5px] text-text-muted">All active submissions across brokers and lines of business.</p>
        </div>
        <uw-button variant="primary" size="sm" icon="plus" (click)="newSubmission()">New submission</uw-button>
      </div>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        @for (s of stats; track s.label) {
          <div class="rounded-xl border border-line bg-surface p-4 shadow-sm">
            <p class="typo-label">{{ s.label }}</p>
            <p class="mt-1 typo-mono text-[22px] font-bold text-primary">{{ s.value }}</p>
            @if (s.delta) {
              <p
                class="mt-0.5 flex items-center gap-1 text-[11px]"
                [class]="s.dir === 'up' ? 'text-success' : s.dir === 'down' ? 'text-warning' : 'text-text-muted'"
              >
                @if (s.dir === 'up') {
                  <uw-icon name="trending-up" [size]="11" />
                } @else if (s.dir === 'down') {
                  <uw-icon name="trending-down" [size]="11" />
                }
                {{ s.delta }}
              </p>
            }
          </div>
        }
      </div>

      <div class="flex flex-wrap items-center gap-1.5">
        @for (chip of chips; track chip.key) {
          <button
            class="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors"
            [class]="activeChip() === chip.key ? 'border-primary/40 bg-primary-soft text-primary' : 'border-line-strong text-secondary hover:bg-surface-sunken hover:text-primary'"
            (click)="setChip(chip.key)"
          >
            {{ chip.label }}
            <span class="typo-mono">· {{ chip.count }}</span>
          </button>
        }
        <div class="ml-auto flex min-w-52 items-center gap-1.5 rounded-lg border border-line bg-surface-sunken/60 px-2.5 py-1.5">
          <uw-icon name="search" [size]="13" class="shrink-0 text-text-muted" />
          <input
            #q
            class="w-full bg-transparent text-[11.5px] text-primary placeholder:text-text-muted focus:outline-none"
            placeholder="Filter by client, broker…"
            [value]="query()"
            (input)="query.set(q.value)"
            aria-label="Filter submissions"
          />
        </div>
      </div>
      <uw-submission-table-lab />

    </div>
  `,
})
export class ClassicSubmissionsPage {
  private readonly accountStore = inject(AccountStore);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  protected readonly stats = SUBMISSION_STATS;
  protected readonly chips = SUBMISSION_CHIPS;
  protected readonly rows = SUBMISSIONS;

  protected readonly activeChip = signal('all');
  protected readonly query = signal('');

  protected readonly filteredRows = computed(() => {
    const q = this.query().trim().toLowerCase();
    return this.rows.filter((r) => {
      const chipOk =
        this.activeChip() === 'all' ||
        (this.activeChip() === 'mine' && r.assigned === 'RR') ||
        (this.activeChip() === 'team' && r.assigned !== 'RR');
      const queryOk =
        !q ||
        r.insured.toLowerCase().includes(q) ||
        r.broker.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q);
      return chipOk && queryOk;
    });
  });

  protected setChip(key: string): void {
    this.activeChip.set(key);
  }

  protected slaClass(state: Submission['slaState']): string {
    if (state === 'critical') return 'font-bold text-danger';
    if (state === 'warn') return 'font-semibold text-warning';
    return 'text-secondary';
  }

  protected priorityTone(priority: Submission['priority']): RiskTone {
    if (priority === 'high') return 'danger';
    if (priority === 'medium') return 'warning';
    return 'neutral';
  }

  protected priorityLabel(priority: Submission['priority']): string {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  }

  protected premium(row: Submission): string {
    return '$' + row.indicatedPremium.toLocaleString();
  }

  protected open(submission: Submission): void {
    this.accountStore.selectAccount(submission.accountId);
    void this.router.navigate(['/classic/account', submission.accountId]);
  }

  protected newSubmission(): void {
    this.toast.info('New submission', 'Create a submission — choose a broker and product to begin.');
  }
}


/*
      <div class="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full border-collapse text-[12.5px]">
            <thead>
              <tr class="bg-surface-sunken/60">
                <th class="typo-label px-3 py-2.5 text-left">Case #</th>
                <th class="typo-label px-3 py-2.5 text-left">Insured</th>
                <th class="typo-label px-3 py-2.5 text-left">Broker</th>
                <th class="typo-label px-3 py-2.5 text-left">Line of business</th>
                <th class="typo-label px-3 py-2.5 text-right">Indicated premium</th>
                <th class="typo-label px-3 py-2.5 text-left">Phase</th>
                <th class="typo-label px-3 py-2.5 text-left">SLA</th>
                <th class="typo-label px-3 py-2.5 text-left">Assigned</th>
                <th class="typo-label px-3 py-2.5 text-left">Priority</th>
              </tr>
            </thead>
            <tbody>
              @for (row of filteredRows(); track row.id) {
                <tr class="cursor-pointer border-t border-line transition-colors hover:bg-surface-sunken/50" (click)="open(row)">
                  <td class="px-3 py-2.5 align-middle">
                    <span class="typo-mono text-[11px] text-ai">{{ row.id }}</span>
                  </td>
                  <td class="px-3 py-2.5 align-middle font-semibold text-primary">{{ row.insured }}</td>
                  <td class="px-3 py-2.5 align-middle text-secondary">{{ row.broker }}</td>
                  <td class="px-3 py-2.5 align-middle text-secondary">{{ row.lob }}</td>
                  <td class="px-3 py-2.5 align-middle text-right">
                    <span class="typo-mono text-[11.5px] text-primary">{{ premium(row) }}</span>
                  </td>
                  <td class="px-3 py-2.5 align-middle">
                    <uw-badge [tone]="row.phaseTone">{{ row.phase }}</uw-badge>
                  </td>
                  <td class="px-3 py-2.5 align-middle" [class]="slaClass(row.slaState)">{{ row.sla }}</td>
                  <td class="px-3 py-2.5 align-middle">
                    <uw-avatar [initials]="row.assignedInitials" [label]="row.assigned" size="xs" />
                  </td>
                  <td class="px-3 py-2.5 align-middle">
                    <uw-badge [tone]="priorityTone(row.priority)">{{ priorityLabel(row.priority) }}</uw-badge>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
      */